import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

// ─── Reusable Sub-Navigation Dropdown ──────────────────────────
// Replaces horizontal pill/tab rows with a compact dropdown
export interface SubNavItem {
    key: string;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    color?: string;       // text color class e.g. 'text-yellow-300'
    count?: number;        // badge count
    countColor?: string;   // count badge color classes
    suffix?: React.ReactNode; // extra content after label (e.g. LIVE badge)
}

interface SubNavDropdownProps {
    items: SubNavItem[];
    activeKey: string;
    onSelect: (key: string) => void;
    className?: string;
}

export function SubNavDropdown({ items, activeKey, onSelect, className = '' }: SubNavDropdownProps) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const activeItem = items.find(i => i.key === activeKey) || items[0];
    const ActiveIcon = activeItem?.icon;

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    // Close on escape
    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open]);

    return (
        <div ref={ref} className={`relative ${className}`}>
            {/* Trigger */}
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-all duration-150"
            >
                {ActiveIcon && (
                    <ActiveIcon className={`size-3.5 ${activeItem.color || 'text-white/60'}`} />
                )}
                <span className="text-xs font-semibold text-white/80 tracking-wide">
                    {activeItem?.label}
                </span>
                {activeItem?.count !== undefined && activeItem.count > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeItem.countColor || 'bg-white/10 text-white/50'}`}>
                        {activeItem.count}
                    </span>
                )}
                {activeItem?.suffix}
                <ChevronDown
                    className={`size-3 text-white/30 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown */}
            {open && (
                <div
                    className="absolute top-full left-0 mt-1.5 z-50 min-w-[180px] rounded-xl overflow-hidden"
                    style={{
                        background: 'linear-gradient(180deg, rgba(12,12,30,0.99) 0%, rgba(8,8,22,0.99) 100%)',
                        backdropFilter: 'blur(24px)',
                        animation: 'subNavSlideIn 120ms ease-out',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 1px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                        border: '1px solid rgba(139,92,246,0.15)',
                    }}
                >
                    <div className="py-1">
                        {items.map(item => {
                            const isActive = activeKey === item.key;
                            const Icon = item.icon;
                            return (
                                <button
                                    key={item.key}
                                    onClick={() => {
                                        onSelect(item.key);
                                        setOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all duration-100 ${isActive ? 'bg-amber-500/10' : 'hover:bg-white/[0.04]'
                                        }`}
                                >
                                    {Icon && (
                                        <Icon
                                            className={`size-3.5 flex-shrink-0 ${isActive ? (item.color || 'text-amber-400') : 'text-white/25'
                                                }`}
                                        />
                                    )}
                                    <span
                                        className={`text-[11px] font-semibold tracking-wide flex-1 ${isActive ? 'text-amber-400' : 'text-white/45'
                                            }`}
                                    >
                                        {item.label}
                                    </span>
                                    {item.count !== undefined && item.count > 0 && (
                                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isActive
                                                ? (item.countColor || 'bg-amber-500/20 text-amber-400')
                                                : (item.countColor || 'bg-white/10 text-white/30')
                                            }`}>
                                            {item.count}
                                        </span>
                                    )}
                                    {item.suffix}
                                    {isActive && (
                                        <span
                                            className="w-1 h-1 rounded-full bg-amber-400 animate-pulse flex-shrink-0"
                                            style={{ boxShadow: '0 0 4px rgba(245,158,11,0.8)' }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            <style>{`
        @keyframes subNavSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
}
